import React, { createContext, useCallback, useContext } from 'react';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import useAccount from '../hooks/account/useAccount';

const PermissionGateContext = createContext();

const PermissionGateProvider = ({ children }) => {
  const { config } = useAccount();
  const userPrivileges = useContext(PrivilegesContext);

  const checkUserPrivileges = useCallback(
    (requiredPrivileges = [], atLeastOneRequired = true) => {
      if (!requiredPrivileges.length) {
        return true;
      }

      const hasPrivilege = atLeastOneRequired
        ? requiredPrivileges.some((privilege) => !!userPrivileges[privilege])
        : requiredPrivileges.every((privilege) => !!userPrivileges[privilege]);

      return hasPrivilege;
    },
    [userPrivileges]
  );

  const checkAccountPermissions = useCallback(
    (accountSettings = {}) => {
      if (!Object.keys(accountSettings).length) {
        return true;
      }

      let hasAccountPermission = true;
      const { allowed = [], notAllowed = [] } = accountSettings;

      if (allowed.length) {
        hasAccountPermission = allowed.every((accountSettingKey) => !!config[accountSettingKey]);
      }

      if (notAllowed.length) {
        hasAccountPermission = notAllowed.every((accountSettingKey) => !config[accountSettingKey]);
      }

      return hasAccountPermission;
    },
    [config]
  );

  const actionPermissionGate = useCallback(
    (action = {}, callback = undefined) => (...params) => {
      const { privileges = [], accountSettings = {}, atLeastOneRequired = true } = action;

      const hasPrivilege = checkUserPrivileges(privileges, atLeastOneRequired);
      const hasPermission = checkAccountPermissions(accountSettings);

      if (hasPrivilege && hasPermission) {
        if (!callback) {
          return true;
        }

        return callback(...params);
      }

      return false;
    },
    [checkUserPrivileges, checkAccountPermissions]
  );

  const readPermissionGate = useCallback(
    (read = {}) => {
      const { privileges = [], atLeastOneRequired = true } = read;

      const hasPrivilege = checkUserPrivileges(privileges, atLeastOneRequired);
      return hasPrivilege;
    },
    [checkUserPrivileges]
  );

  const contextMenuPermissionGate = (menuOption = {}, disabledKey = 'isDisabled') => {
    if (menuOption?.readRequiredPrivilege) {
      const readPermission = readPermissionGate({ privileges: menuOption?.readRequiredPrivilege });
      menuOption.visible = (menuOption.visible !== undefined ? menuOption.visible : true) ? readPermission : false;
    }

    if (menuOption?.actionRequiredPrivilege || menuOption?.actionAccountSettings) {
      const actionPermission = actionPermissionGate({
        privileges: menuOption?.actionRequiredPrivilege,
        accountSettings: menuOption?.actionAccountSettings,
        atLeastOneRequired: menuOption?.atLeastOneRequired
      })();
      menuOption[disabledKey] = menuOption[disabledKey] || !actionPermission;
    }

    return menuOption;
  };

  const value = { actionPermissionGate, readPermissionGate, contextMenuPermissionGate };

  return <PermissionGateContext.Provider value={value}>{children}</PermissionGateContext.Provider>;
};

export const usePermissionGate = () => {
  const context = useContext(PermissionGateContext);

  if (!context) {
    throw new Error('usePermissionGate must be used within a PermissionGateProvider');
  }

  return context;
};

export { PermissionGateContext, PermissionGateProvider };
